/**
 * One record as a card, for the places a table is the wrong shape.
 *
 * The card view of a list, the module picker, the tenant master list: all three
 * had written the same bordered box with a header row, a body and a footer, and
 * they disagreed on which parts were optional.
 *
 * `selected` marks it `aria-current`: every master list built on it puts the
 * selection in the URL, so picking a card moves to it rather than toggling it.
 *
 * @module components/ui/EntityCard
 */

import React, { type ReactNode } from "react";

import { Icon } from "./Icon";

export interface EntityCardProps {
  /** What the record is called. */
  title: ReactNode;
  /** Its identifier or classification, above the title. */
  eyebrow?: ReactNode;
  /** One line under the title: where it sits, how it is delivered. */
  meta?: ReactNode;
  children?: ReactNode;
  /** A divided row at the bottom, for status and the way in. */
  footer?: ReactNode;
  /** Makes the whole card a control. Requires `onSelect`. */
  selected?: boolean;
  onSelect?: () => void;
  /**
   * Makes the whole card open something while the footer stays live. `onSelect`
   * wraps everything in one button, so a menu in the footer would nest inside
   * it; this puts the control on the title and stretches it over the card, so
   * the card is the target and the footer's own controls sit above it.
   */
  onOpen?: () => void;
  /**
   * What the control is called, where reading the card aloud would not say it.
   * A card carrying status chips names the record; without this the button
   * announces every chip concatenated into one run-on string.
   */
  "aria-label"?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <EntityCard eyebrow="SES901" title="Energy Systems Analysis" meta="Stage 1 · Semester 1" />
 * ```
 */
export function EntityCard({
  title,
  eyebrow = undefined,
  meta = undefined,
  children = undefined,
  footer = undefined,
  selected = undefined,
  onSelect = undefined,
  onOpen = undefined,
  "aria-label": label = undefined,
  className = "",
  "data-testid": testId,
}: EntityCardProps): React.ReactElement {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {eyebrow != null && <div className="mb-1 text-xs text-muted">{eyebrow}</div>}
          <div className="font-semibold text-base-content">
            {onOpen ? (
              <button
                type="button"
                className="text-left after:absolute after:inset-0 after:content-['']"
                onClick={onOpen}
                data-testid={testId ? `${testId}-open` : undefined}
              >
                {title}
              </button>
            ) : (
              title
            )}
          </div>
          {meta != null && <div className="mt-0.5 text-md text-muted">{meta}</div>}
        </div>
      </div>
      {children != null && <div className="text-md text-muted">{children}</div>}
      {footer != null && (
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-2 border-t border-base-300 pt-2">
          {footer}
        </div>
      )}
      {onOpen && (
        <Icon
          name="caret-right"
          className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-muted"
          aria-hidden
        />
      )}
    </>
  );

  const shell = [
    "flex flex-col gap-2.5 rounded-box border p-4 text-left transition-colors duration-150",
    onOpen ? "relative pr-10" : "",
    selected
      ? "border-base-content bg-base-100"
      : "border-base-300 bg-base-100 hover:border-border-strong hover:bg-surface-hover",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (!onSelect) {
    return (
      <div className={shell} data-testid={testId}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      // `aria-current`, not `aria-pressed`: pressing the selected card again
      // does not unselect it, so it is not a toggle.
      aria-current={selected ? "true" : undefined}
      aria-label={label}
      onClick={onSelect}
      className={shell}
      data-testid={testId}
    >
      {body}
    </button>
  );
}
